import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';

/**
 * State for geometry node editing mode.
 *
 * Tracks whether node editing is active, which feature/layer is being edited,
 * and which node (if any) is currently selected for manipulation.
 *
 * **Validates: Requirement 16.1** – WHEN a Registered_User selects a Feature
 * and activates node editing mode, THE Geometry_Editor SHALL display all nodes
 * as editable points.
 */
export interface GeometryEditingState {
  /** Whether node editing mode is currently active */
  isEditingGeometry: boolean;
  /** The ID of the feature currently being edited */
  editingFeatureId: string | null;
  /** The ID of the layer containing the feature being edited */
  editingLayerId: string | null;
  /** The index of the currently selected node (null if no node selected) */
  selectedNodeIndex: number | null;
  /** Whether the polygon split tool is currently active */
  isSplitToolActive: boolean;
  /** The ID of the feature targeted for splitting */
  splitFeatureId: string | null;
  /** The ID of the layer containing the feature targeted for splitting */
  splitLayerId: string | null;
  /** The drawn split line coordinates (EPSG:4326) after drawend, null if not yet drawn */
  splitLineCoordinates: number[][] | null;
}

const initialState: GeometryEditingState = {
  isEditingGeometry: false,
  editingFeatureId: null,
  editingLayerId: null,
  selectedNodeIndex: null,
  isSplitToolActive: false,
  splitFeatureId: null,
  splitLayerId: null,
  splitLineCoordinates: null,
};

/**
 * Payload for activating node editing mode on a specific feature.
 */
export interface ActivateNodeEditingPayload {
  featureId: string;
  layerId: string;
}

/**
 * Payload for activating the polygon split tool on a specific feature.
 */
export interface ActivateSplitToolPayload {
  featureId: string;
  layerId: string;
}

const geometryEditingSlice = createSlice({
  name: 'geometryEditing',
  initialState,
  reducers: {
    /**
     * Activate node editing mode for a specific feature.
     *
     * Requires both a featureId and layerId to identify the target feature.
     * Resets any previously selected node.
     */
    activateNodeEditing: (state, action: PayloadAction<ActivateNodeEditingPayload>) => {
      state.isEditingGeometry = true;
      state.editingFeatureId = action.payload.featureId;
      state.editingLayerId = action.payload.layerId;
      state.selectedNodeIndex = null;
    },

    /**
     * Deactivate node editing mode and return to normal interaction.
     *
     * Clears all editing state including the selected node.
     */
    deactivateNodeEditing: (state) => {
      state.isEditingGeometry = false;
      state.editingFeatureId = null;
      state.editingLayerId = null;
      state.selectedNodeIndex = null;
    },

    /**
     * Select a specific node by its index for manipulation (move/delete).
     */
    selectNode: (state, action: PayloadAction<number>) => {
      state.selectedNodeIndex = action.payload;
    },

    /**
     * Clear the currently selected node without leaving editing mode.
     */
    clearSelectedNode: (state) => {
      state.selectedNodeIndex = null;
    },

    /**
     * Activate the polygon split tool for a specific feature.
     *
     * Deactivates node editing if it was active, since split tool and
     * node editing are mutually exclusive modes.
     *
     * **Validates: Requirement 14.1** – WHEN a Registered_User selects a
     * polygon Feature and activates split tool, THE Vector_Editor SHALL
     * enable drawing a split line.
     */
    activateSplitTool: (state, action: PayloadAction<ActivateSplitToolPayload>) => {
      // Deactivate node editing if active
      state.isEditingGeometry = false;
      state.editingFeatureId = null;
      state.editingLayerId = null;
      state.selectedNodeIndex = null;
      // Activate split tool
      state.isSplitToolActive = true;
      state.splitFeatureId = action.payload.featureId;
      state.splitLayerId = action.payload.layerId;
    },

    /**
     * Deactivate the polygon split tool and return to normal interaction.
     */
    deactivateSplitTool: (state) => {
      state.isSplitToolActive = false;
      state.splitFeatureId = null;
      state.splitLayerId = null;
      state.splitLineCoordinates = null;
    },

    /**
     * Store the drawn split line coordinates after the user finishes drawing.
     * Coordinates are in EPSG:4326 (lon/lat).
     *
     * **Validates: Requirement 14.1** – enables drawing a split line across
     * the selected polygon.
     */
    setSplitLineCoordinates: (state, action: PayloadAction<number[][]>) => {
      state.splitLineCoordinates = action.payload;
    },

    /**
     * Clear the split line coordinates (e.g., to allow re-drawing).
     */
    clearSplitLineCoordinates: (state) => {
      state.splitLineCoordinates = null;
    },
  },
});

export const {
  activateNodeEditing,
  deactivateNodeEditing,
  selectNode,
  clearSelectedNode,
  activateSplitTool,
  deactivateSplitTool,
  setSplitLineCoordinates,
  clearSplitLineCoordinates,
} = geometryEditingSlice.actions;

export default geometryEditingSlice.reducer;

/* -------------------------------------------------------------------------- */
/*  Selectors                                                                 */
/* -------------------------------------------------------------------------- */

/** Raw slice state. */
const selectGeometryEditingState = (state: RootState): GeometryEditingState =>
  state.geometryEditing;

/** Whether node editing mode is active. */
export const selectIsEditingGeometry = createSelector(
  selectGeometryEditingState,
  (editing) => editing.isEditingGeometry
);

/** The feature ID currently being edited (null if not editing). */
export const selectEditingFeatureId = createSelector(
  selectGeometryEditingState,
  (editing) => editing.editingFeatureId
);

/** The layer ID of the feature being edited (null if not editing). */
export const selectEditingLayerId = createSelector(
  selectGeometryEditingState,
  (editing) => editing.editingLayerId
);

/** The currently selected node index (null if no node selected). */
export const selectSelectedNodeIndex = createSelector(
  selectGeometryEditingState,
  (editing) => editing.selectedNodeIndex
);

/** Whether a specific node is currently selected. */
export const selectHasSelectedNode = createSelector(
  selectSelectedNodeIndex,
  (index) => index !== null
);

/** Whether the polygon split tool is currently active. */
export const selectIsSplitToolActive = createSelector(
  selectGeometryEditingState,
  (editing) => editing.isSplitToolActive
);

/** The feature ID targeted for splitting (null if split tool not active). */
export const selectSplitFeatureId = createSelector(
  selectGeometryEditingState,
  (editing) => editing.splitFeatureId
);

/** The layer ID of the feature targeted for splitting (null if split tool not active). */
export const selectSplitLayerId = createSelector(
  selectGeometryEditingState,
  (editing) => editing.splitLayerId
);

/** The drawn split line coordinates (null if not yet drawn). */
export const selectSplitLineCoordinates = createSelector(
  selectGeometryEditingState,
  (editing) => editing.splitLineCoordinates
);
